import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UpdateQuestionValuesQuoteFormSummaryRequest, upsertQuoteFormSummaryQuote, getQuoteFormSummaryQuoteSummary } from '../api/quoteFormSummaryService';
import { getCurrentSignatureRef, getSamlLogalty, requestFrameLogalty, SamlRequest, SignatureRequest } from '../api/logaltyService';
import { getApplicationByQuoteId, ApplicationStatus } from '../api/applicationService';
import { REACT_APP_BUCKET_DIGITAL_SIGNATURE, REACT_APP_COUNTRY, REACT_APP_SIGNATURE_BILLING_COST, REACT_APP_SIGNATURE_CALLBACK, REACT_APP_SIGNATURE_TIME2CLOSE, REACT_APP_SIGNATURE_TIME2SAVE, REACT_APP_URL_LOGALTY_SIGNATURE, REACT_APP_PAYMENT_WEB_URL } from '../config';
import { allowsApplicationWithoutLegacyQuoteForm } from '../intake/pathConfig';
import { probeIntakeSession } from '../api/intakeSessionService';
import { getIntakeApplicationStatus } from '../api/intakeApplicationService';
import { isOnAnyStatusScreen, portalBoardUrl as clientPortalBoardUrl } from '../clientPortal/portalEntry';
import { resolveIntakeResumeIntercept } from '../intake/resume/resolveIntakeResumeIntercept';
import { resolveSignedTarget, resolveSummaryStepTarget } from './resume/resolveLegacyResume';
import { confirmationTraductor } from '../traductor/confirmation';
import { createLogger } from '../utils/logger';
import { trackApplicationSigned, flushAmplitude } from '../utils/amplitude';
import { resolveResumeRoute, ResumeRoute } from '../utils/applicationResumeRoute';
import { holdErrorQueryValue, stripRetryParamFromLocation } from '../utils/signatureAttempt';
import { QuoteContext } from './quoteContext';
import { isSpaStatusTarget } from './quoteLoadGuard';

// Loggers específicos para diferentes funcionalidades
const stepLogger = createLogger('QUOTE_FORM_STEP');
const dataLogger = createLogger('QUOTE_FORM_DATA');
const apiLogger = createLogger('QUOTE_FORM_API');
const signatureLogger = createLogger('DIGITAL_SIGNATURE');
const applicationLogger = createLogger('APPLICATION_STATUS');

// StrictMode runs the boot effect twice before either setState lands. A ref
// resets on that remount; this set does not, so the second run does not start
// another application → summary chain.
const summaryStepInflight = new Map<string, Promise<void>>();

// Constantes para firma digital
const PROVIDER_BR = 'DOCUSIGN';
const PROVIDER_MX = 'LOGALTY';
const SIGN_METHOD = 'OTP';
const SIGN_CHANNEL = 'SMS';
const IS_SYNCHRONOUS = true;
const DOCUMENT_TYPE = 'CPF';
const DOCUMENT_TYPE_MX: any = {
    '1': 'INE',
    '3': 'Certificado de Matrícula Consular',
    '6': 'Pasaporte',
}

// Constante para el segmento de carpeta de clientes (igual que en signaturePage)
const CLIENTS_FOLDER_SEGMENT = '/clients';

const QUOTE_PATH = '/quote';
const APPLICATION_PATH = '/application';
// Pantallas que montan la ceremonia de firma por su cuenta (MX Logalty, BR DocuSign). Estando en
// ellas, la ceremonia tiene un dueño y no es este contexto.
const SIGNATURE_PATHS = ['/logalty', '/signature'];
// Misma clave que persiste QuoteContext.setStartFromBeginning
const START_FROM_BEGINNING_KEY = 'startFromBeginning';

// Función para enviar SAML mediante formulario POST (igual que signaturePage)
const submitSamlForm = (samlValue: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = REACT_APP_URL_LOGALTY_SIGNATURE;
    form.style.display = 'none';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'saml_assertion';
    input.value = samlValue;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
}

export interface QuoteFormSummaryContextType {
    quoteFormSummaryQuoteData: UpdateQuestionValuesQuoteFormSummaryRequest;
    updateQuoteFormSummaryField: (field: keyof UpdateQuestionValuesQuoteFormSummaryRequest, value: any) => void;
    updateMultipleQuoteFormSummaryFields: (fields: Partial<UpdateQuestionValuesQuoteFormSummaryRequest>) => void;
    saveQuoteFormSummaryQuote: (id: string, data?: UpdateQuestionValuesQuoteFormSummaryRequest) => Promise<void>;
    resetQuoteFormSummaryData: () => void;
    isCheckingQuoteFormSummaryStep: boolean;
    quoteFormSummaryStepChecked: boolean;
    quoteFormSummaryStep: string | null;
    isRedirectingToDigitalSignature: boolean;
    setIsRedirectingToDigitalSignature: (value: boolean) => void;
    isRedirectingToApplication: boolean;
}

const QuoteFormSummaryContext = createContext<QuoteFormSummaryContextType | undefined>(undefined);

export const useQuoteFormSummaryContext = () => {
    const context = useContext(QuoteFormSummaryContext);
    if (!context) {
        throw new Error('useQuoteFormSummaryContext debe ser usado dentro de un QuoteFormSummaryProvider');
    }
    return context;
};

interface QuoteFormSummaryProviderProps {
    children: ReactNode;
}

export const QuoteFormSummaryProvider: React.FC<QuoteFormSummaryProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const { setStartFromBeginning } = useContext(QuoteContext);
    const [quoteFormSummaryQuoteData, setQuoteFormSummaryQuoteData] = useState<UpdateQuestionValuesQuoteFormSummaryRequest>({});
    const [isCheckingQuoteFormSummaryStep, setIsCheckingQuoteFormSummaryStep] = useState(false);
    const [quoteFormSummaryStepChecked, setQuoteFormSummaryStepChecked] = useState(false);
    const [quoteFormSummaryStep, setQuoteFormSummaryStep] = useState<string | null>(null);
    const [isRedirectingToDigitalSignature, setIsRedirectingToDigitalSignature] = useState(false);
    const [isRedirectingToApplication, setIsRedirectingToApplication] = useState(false);


    useEffect(() => {
        // Obtener quoteID de los query params
        const queryParams = new URLSearchParams(window.location.search);
        const quoteIDFromURL = queryParams.get('quoteID');

        // Verificar step Quote Form Summary cuando tengamos un quoteID y no se haya chequeado antes
        if (quoteIDFromURL && !quoteFormSummaryStepChecked) {
            checkQuoteFormSummaryQuoteStep(quoteIDFromURL);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteFormSummaryStepChecked]); // Depender solo de quoteFormSummaryStepChecked para evitar re-chequeos innecesarios

    // /status/* is outside QuoteProvider (App.tsx), so a router transition
    // unmounts the providers and does not boot the quote twice. Every other
    // resume target reloads the document: confirmation, attachment and
    // signature (MX) and application, confirmation and signature (BR) only
    // update application status after QuoteFormSummaryProvider remounts with
    // quoteFormSummaryStepChecked still false. A client-side navigate leaves
    // that flag false on the same instance, and those pages never write.
    const openTarget = (target: string) => {
        if (isSpaStatusTarget(target)) {
            navigate(target);
            return;
        }
        window.location.href = target;
    };

    const isOnSignaturePage = (): boolean =>
        SIGNATURE_PATHS.some(path => window.location.pathname.includes(path));

    const handleDigitalSignatureMX = async (quoteID: string, summaryData: any) => {
        try {
            setIsRedirectingToDigitalSignature(true);
            signatureLogger.info('Iniciando proceso de firma digital para México, quoteID:', quoteID);

            const samlRequest: SamlRequest = {
                ref_id: await getCurrentSignatureRef(quoteID),
            }

            // Usar el mismo formato de folder que signaturePage: MX/clients/quoteID/
            const data: SignatureRequest = {
                "ref_id": quoteID,
                "entity_type": 'client_application',
                "quote_id": quoteID,
                "bucket": `${REACT_APP_BUCKET_DIGITAL_SIGNATURE}`,
                "folder": `MX${CLIENTS_FOLDER_SEGMENT}/${quoteID}/`,
                "subject": '-',
                "body": '-',
                "synchronous": IS_SYNCHRONOUS,
                "client_url": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "back_url": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "url_back": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "locale": "es_ES",
                "sender_email": summaryData.email,
                "language": "es",
                "file_mappings": [
                    {
                        "search_name": "summary.pdf",
                        "target_file_name": "Resumen.pdf"
                    },
                    {
                        "search_name": "privacy_notice.pdf",
                        "target_file_name": "AvisoDePrivacidad.pdf"
                    },
                    {
                        "search_name": "terms_and_conditions.pdf",
                        "target_file_name": "TerminosYCondiciones.pdf"
                    }
                ],
                "contacts": [
                    {
                        "document_type": DOCUMENT_TYPE_MX[summaryData.document_type] || '',
                        "document_number": summaryData.document_number || '',
                        "first_name": summaryData.first_name || '',
                        "middle_name": summaryData.middle_name || '',
                        "last_name1": summaryData.last_name || '',
                        "last_name2": summaryData.second_last_name || '',
                        "country": "MX", // Hardcodeado igual que signaturePage
                        "email": summaryData.email || '',
                        "phone": (summaryData.phone || '').replace(/[\s-]/g, ''),
                        "mobile": (summaryData.phone || '').replace(/[\s-]/g, ''),
                        "sign_method": SIGN_METHOD,
                        "sign_channel": SIGN_CHANNEL
                    }
                ],
                "time2save_days": Number.parseInt(`${REACT_APP_SIGNATURE_TIME2SAVE}`),
                "time2close_days": Number.parseInt(`${REACT_APP_SIGNATURE_TIME2CLOSE}`),
                "frame_url": 'LOGALTY_DIRECT_ACCESS_DOC_IN_FRAME',
                "billing_cost": `${REACT_APP_SIGNATURE_BILLING_COST}`
            };

            // Primero intentar obtener SAML existente
            const samlContent = await getSamlLogalty(samlRequest, 'LOGALTY');
            if (samlContent && samlContent.saml_value) {
                signatureLogger.info('SAML existente encontrado, enviando mediante POST');
                submitSamlForm(samlContent.saml_value);
                return;
            }

            // Si no hay SAML existente, crear uno nuevo
            const response = await requestFrameLogalty(data, PROVIDER_MX);

            if (response && response.saml_value) {
                signatureLogger.info('Nuevo SAML obtenido, enviando mediante POST');
                submitSamlForm(response.saml_value);
            } else {
                signatureLogger.error('Respuesta de Logalty no contiene saml_value:', response);
            }
        } catch (error) {
            signatureLogger.error('Error al iniciar firma digital:', error);
        }
    };

    const handleDigitalSignatureBR = async (quoteID: string, summaryData: any) => {
        try {
            setIsRedirectingToDigitalSignature(true);
            signatureLogger.info('Iniciando proceso de firma digital para Brasil, quoteID:', quoteID);

            const folder = `${REACT_APP_COUNTRY}/clients/`;
            const data: SignatureRequest = {
                "ref_id": quoteID,
                "entity_type": 'client_application',
                "quote_id": quoteID,
                "bucket": `${REACT_APP_BUCKET_DIGITAL_SIGNATURE}`,
                "folder": `${folder}${quoteID}/`,
                "subject": `${confirmationTraductor('pt', 'emailSubject')}`,
                "body": `${confirmationTraductor('pt', 'emailBody')}`,
                "synchronous": IS_SYNCHRONOUS,
                "client_url": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "back_url": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "url_back": `${REACT_APP_SIGNATURE_CALLBACK}${quoteID}`,
                "locale": "es_ES",
                "sender_email": summaryData.email,
                "contacts": [
                    {
                        "document_type": DOCUMENT_TYPE,
                        "document_number": summaryData.document_number,
                        "first_name": summaryData.first_name,
                        "middle_name": summaryData.middle_name,
                        "last_name1": summaryData.last_name,
                        "last_name2": summaryData.second_last_name,
                        "country": summaryData.country,
                        "email": summaryData.email,
                        "phone": summaryData.phone?.replace(/[\s-]/g, ''),
                        "mobile": summaryData.phone?.replace(/[\s-]/g, ''),
                        "sign_method": SIGN_METHOD,
                        "sign_channel": SIGN_CHANNEL,
                        "sign_fields": {
                            "sign_field": "/firma_1/",
                            "date_field": "/fecha_firma/",
                            "additional_fields": [
                                {
                                    "field": "/nombre_apellido_1/",
                                    "name": "Nome Completo",
                                    "label": "NombreApellido"
                                }
                            ]
                        }
                    }
                ],
                "language": "pt",
                "time2save_days": Number.parseInt(`${REACT_APP_SIGNATURE_TIME2SAVE}`),
                "time2close_days": Number.parseInt(`${REACT_APP_SIGNATURE_TIME2CLOSE}`),
                "billing_cost": `${REACT_APP_SIGNATURE_BILLING_COST}`
            };
            const response = await requestFrameLogalty(data, PROVIDER_BR);

            if (response && response.action) {
                signatureLogger.info('Firma digital iniciada exitosamente, redirigiendo a:', response.action);
                openTarget(response.action);
            }
        } catch (error) {
            signatureLogger.error('Error al iniciar firma digital:', error);
        }
    };

    const handleDigitalSignature = async (quoteID: string, summaryData: any) => {
        if (REACT_APP_COUNTRY === 'MX') {
            await handleDigitalSignatureMX(quoteID, summaryData);
        } else {
            await handleDigitalSignatureBR(quoteID, summaryData);
        }
    };

    const signatureHoldUrl = (quoteID: string, signatureError?: string | null): string => {
        const id = encodeURIComponent(quoteID);
        const error = encodeURIComponent(holdErrorQueryValue(signatureError));
        if (REACT_APP_COUNTRY === 'BR') {
            return `/signature-rejected?error=${error}&quoteID=${id}`;
        }
        return `/logalty?error=${error}&quoteID=${id}`;
    };

    const buildResumeUrl = async (route: ResumeRoute, quoteID: string): Promise<string> => {
        const params = new URLSearchParams(route.params ?? {});
        if (route.identifiesByEmail) {
            const summary = await getQuoteFormSummaryQuoteSummary(quoteID);
            if (summary?.email) {
                params.set('email', summary.email);
            }
        } else {
            params.set('quoteID', quoteID);
        }
        const query = params.toString();
        return query ? `${route.path}?${query}` : route.path;
    };

    const checkApplicationStatusAndRedirect = async (
        quoteID: string,
        explicitRetry: boolean,
    ): Promise<{ stop: boolean; status?: string }> => {
        try {
            applicationLogger.info('Verificando status de la aplicación para quoteID:', quoteID);

            const response = await getApplicationByQuoteId(quoteID);
            const found = Boolean(response.found && response.data);
            const status = response.data?.status;

            if (found) {
                applicationLogger.info('Status de la aplicación:', status);
            } else {
                applicationLogger.info('No existe aplicación para este quoteID');
            }

            // Probe A2 solo para el candidato S1 (`in summary`). Post-accept delega
            // aunque haya sesión; un probe que falla no elige path (queda `error` → delegate).
            let intakeSession: 'present' | 'absent' | 'error' = 'absent';
            if (found && status === ApplicationStatus.IN_SUMMARY) {
                try {
                    intakeSession = (await probeIntakeSession(quoteID)).status;
                } catch (probeErr) {
                    applicationLogger.warn(
                        'Probe sesión A2 falló en resume in summary; fallback legacy',
                        probeErr,
                    );
                    intakeSession = 'error';
                }
            }

            // The intake machine, not quote_form.status, decides the client portal.
            // MX/BR ask only when the quote is signed, so a submitted new journey
            // does not open the legacy payment site. INT asks on every resume:
            // a machine status other than `pending_data_entry` opens the client
            // portal. No row or a failed query leaves the flag false, and resume
            // falls back to the editable form or the summary.
            let machineBeyondDataEntry = false;
            const intResume = REACT_APP_COUNTRY !== 'MX' && REACT_APP_COUNTRY !== 'BR';
            if (intResume || (found && status === ApplicationStatus.SIGNED)) {
                try {
                    const machine = await getIntakeApplicationStatus(quoteID);
                    machineBeyondDataEntry = Boolean(
                        machine && machine.status && machine.status !== 'pending_data_entry',
                    );
                } catch (machineErr) {
                    applicationLogger.warn('Consulta de máquina intake falló (fail-open legacy)', machineErr);
                }
            }

            const intercept = resolveIntakeResumeIntercept({
                found,
                status,
                quoteID,
                pathname: window.location.pathname,
                search: window.location.search,
                allowsSchemaDrivenWithoutQuoteForm: allowsApplicationWithoutLegacyQuoteForm(),
                intakeSession,
                machineBeyondDataEntry,
            });

            if (intercept.action === 'redirect') {
                applicationLogger.info(`Resume intake (${intercept.reason}), redirigiendo a:`, intercept.target);
                // Journey nuevo firmado: este redirect sale antes del `track` de la rama
                // legacy de abajo, así que el evento terminal del funnel se manda acá.
                if (status === ApplicationStatus.SIGNED && trackApplicationSigned(quoteID)) {
                    await flushAmplitude();
                }
                openTarget(intercept.target);
                return { stop: true, status };
            }
            if (intercept.action === 'stay') {
                applicationLogger.info('Resume intake sin redirección:', intercept.reason);
                return { stop: false, status };
            }

            if (!found) {
                const currentPath = window.location.pathname;
                const isOnApplicationOrConfirmation =
                    currentPath.includes('/application') || currentPath.includes('/confirmation');
                if (isOnApplicationOrConfirmation) {
                    applicationLogger.info('No existe aplicación, redirigiendo a quote');
                    openTarget(`/quote?quoteID=${quoteID}`);
                    return { stop: true };
                }
                return { stop: false };
            }

            const searchParams = new URLSearchParams(window.location.search);
            const signatureError = searchParams.get('error');
            // retry=1 is the explicit "Reintentar firma" navigation. Without it, a
            // closed attempt on /signature (BR) would bounce back to the hold screen.
            // The caller already removed it from the address bar for this check only.
            const resumeRoute = resolveResumeRoute(status, REACT_APP_COUNTRY, {
                signatureError,
                explicitRetry,
            });
            if (resumeRoute && !window.location.pathname.includes(resumeRoute.path)) {
                applicationLogger.info('Retomando la solicitud en:', resumeRoute.path, 'motivo:', resumeRoute.reason);
                if (resumeRoute.path === APPLICATION_PATH) {
                    setIsRedirectingToApplication(true);
                }
                openTarget(await buildResumeUrl(resumeRoute, quoteID));
                return { stop: true, status };
            }

            // MX y BR: el status `signed` no está en el mapa. MX abre la solicitud
            // enviada y BR la sesión de pago. INT no decide por quote_form.status:
            // sin máquina fuera de la carga de datos, el fallback es el formulario
            // o el resumen, no la web de pagos.
            if (!intResume && status === ApplicationStatus.SIGNED) {
                applicationLogger.info('Aplicación con estado SIGNED, resolviendo destino post-firma...');
                trackApplicationSigned(quoteID);
                const amplitudeFlushed = flushAmplitude();
                const quoteFormSummarySummary = await getQuoteFormSummaryQuoteSummary(quoteID);
                await amplitudeFlushed;
                const signed = resolveSignedTarget({
                    country: REACT_APP_COUNTRY,
                    paymentSession: quoteFormSummarySummary?.payment_session,
                    fallbackUrl: REACT_APP_PAYMENT_WEB_URL,
                    email: quoteFormSummarySummary?.email,
                });
                applicationLogger.info(`Redirigiendo a (${signed.reason}):`, signed.target);
                openTarget(signed.target);
                return { stop: true, status };
            }

            applicationLogger.info('Resume sin redirección:', intercept.reason);
            return { stop: false, status };
        } catch (error) {
            applicationLogger.error('Error al verificar status de la aplicación:', error);
            return { stop: false };
        }
    };

    const checkQuoteFormSummaryQuoteStep = async (quoteID: string) => {
        const explicitRetry = new URLSearchParams(window.location.search).get('retry') === '1';
        const pending = summaryStepInflight.get(quoteID);
        if (pending) {
            if (!explicitRetry) return;
            await pending.catch(() => undefined);
        } else if (isCheckingQuoteFormSummaryStep && !explicitRetry) {
            return;
        }

        let release: () => void = () => {};
        const gate = new Promise<void>((resolve) => {
            release = resolve;
        });
        summaryStepInflight.set(quoteID, gate);
        setIsCheckingQuoteFormSummaryStep(true);
        // Evita que los useEffects de las páginas actualicen el status cuando la aplicación
        // ya se encuentra en un estado final (SIGNED, paid, rejected) o está siendo redirigida.
        let shouldSkipStatusUpdate = false;
        try {
            // El flag persiste en localStorage y sobrevive al cierre de la pestaña, así que un
            // "volver a la cotización" abandonado deja al postulante clavado en la primera página
            // la próxima vez que abra su link. Entrar a /quote es siempre un reingreso: el flag
            // solo vale para el "continuar" que venga después, que lo vuelve a poner.
            // Client-side navigate does not remount QuoteProvider, so clearing
            // storage is not enough: the in-memory flag has to drop too.
            if (window.location.pathname.includes(QUOTE_PATH)) {
                localStorage.removeItem(START_FROM_BEGINNING_KEY);
                setStartFromBeginning(false);
            }

            // Read once above. A ref that stayed true would make a later in-sign
            // visit in the same session bill another ceremony.
            if (explicitRetry) {
                stripRetryParamFromLocation();
            }

            // On the intake path these two reads do not depend on each other.
            // Starting the summary with the application GET keeps it off the
            // tail of that round trip. A redirect still uses the shared request
            // when the signed branch needs the same summary.
            const summaryAhead = allowsApplicationWithoutLegacyQuoteForm()
                ? getQuoteFormSummaryQuoteSummary(quoteID)
                : null;

            const applicationCheck = await checkApplicationStatusAndRedirect(quoteID, explicitRetry);
            if (applicationCheck.stop) {
                shouldSkipStatusUpdate = true;
                summaryAhead?.catch(() => undefined);
                return;
            }

            stepLogger.info('Verificando step de cotización para ID:', quoteID);
            const quoteFormSummarySummary = summaryAhead
                ? await summaryAhead
                : await getQuoteFormSummaryQuoteSummary(quoteID);

            if (!quoteFormSummarySummary) {
                stepLogger.warn('No se encontró cotización para el ID:', quoteID, '- Continuando con flujo normal');
                return;
            }

            setQuoteFormSummaryStep(quoteFormSummarySummary.step);

            // Matriz del step en resolveSummaryStepTarget (testeada).
            const searchParams = new URLSearchParams(window.location.search);
            const signatureError = searchParams.get('error');
            const stepDecision = resolveSummaryStepTarget({
                step: quoteFormSummarySummary.step,
                country: REACT_APP_COUNTRY,
                email: quoteFormSummarySummary.email,
                applicationStatus: applicationCheck.status,
                signatureError,
                explicitRetry,
            });

            // IC5-Q2 (2026-09-04): si la máquina intake dice que la solicitud ya
            // salió de la carga de datos, la matriz del step legacy NO decide el
            // destino — la re-entrada del journey nuevo vive en la pantalla de
            // estado (para `signed` la matriz iría a congratulations/pagos, y el
            // cobro del journey nuevo es post-confirmación — D-15/A31).
            // A cancelled attempt stays on the retry screen. The machine guard would
            // send the client to the board and hide "Reintentar firma".
            if (stepDecision.action !== 'stay' && stepDecision.action !== 'signature_retry') {
                const machine = await getIntakeApplicationStatus(quoteID).catch(() => null);
                const machineBeyond = Boolean(
                    machine && machine.status && machine.status !== 'pending_data_entry',
                );
                if (machineBeyond) {
                    if (!isOnAnyStatusScreen(window.location.pathname)) {
                        stepLogger.info('Máquina intake ya enviada: destino /status/solicitud (matriz legacy ignorada)');
                        openTarget(clientPortalBoardUrl(quoteID));
                    } else {
                        stepLogger.info('Máquina intake ya enviada: quieto en pantalla de estado');
                    }
                    return;
                }
            }

            if (stepDecision.action === 'signature_retry') {
                if (isOnSignaturePage()) {
                    stepLogger.info('Firma cancelada en la pantalla de firma: la página muestra el reintento, sin SAML');
                    return;
                }
                const holdUrl = signatureHoldUrl(quoteID, signatureError);
                stepLogger.info('Firma cancelada: no se reabre la ceremonia, pantalla de reintento', holdUrl);
                openTarget(holdUrl);
                return;
            }

            if (stepDecision.action === 'digital_signature') {
                // La página de firma arranca su propia ceremonia al montarse. Si el contexto
                // también la arranca, se piden DOS SAML para la misma transacción: Logalty
                // invalida el primero y el form que postea tarde muere con "400 E000311 · error en
                // el proceso". El síntoma es intermitente porque depende de cuál gane la carrera.
                if (isOnSignaturePage()) {
                    stepLogger.info('Step digital_signature en la propia pantalla de firma: la ceremonia la abre la página');
                    return;
                }
                stepLogger.info('Cotización en step digital_signature, iniciando firma digital');
                await handleDigitalSignature(quoteID, quoteFormSummarySummary);
                return;
            }

            if (stepDecision.action === 'redirect') {
                stepLogger.info(`Resume por step (${stepDecision.reason}), redirigiendo a:`, stepDecision.target);
                openTarget(stepDecision.target);
                return;
            }

            stepLogger.info('Cotización en step:', quoteFormSummarySummary.step, '- Continuando con flujo normal');
        } catch (error) {
            stepLogger.error('Error al verificar step de cotización:', error);
            // Si hay error, continuar con el flujo normal
        } finally {
            if (summaryStepInflight.get(quoteID) === gate) summaryStepInflight.delete(quoteID);
            release();
            setIsCheckingQuoteFormSummaryStep(false);
            // Solo marcar como chequeado si el status no es uno final (SIGNED, paid, rejected).
            // Si shouldSkipStatusUpdate es true no se disparan los useEffects de actualización
            // de status en la página actual (IN_SUMMARY, IN_PROGRESS, etc.).
            if (!shouldSkipStatusUpdate) {
                setQuoteFormSummaryStepChecked(true);
            }
        }
    };

    const updateQuoteFormSummaryField = (field: keyof UpdateQuestionValuesQuoteFormSummaryRequest, value: any) => {
        setQuoteFormSummaryQuoteData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateMultipleQuoteFormSummaryFields = (fields: Partial<UpdateQuestionValuesQuoteFormSummaryRequest>) => {
        setQuoteFormSummaryQuoteData(prev => {
            // Combinar los campos existentes con los nuevos
            const updatedData = {
                ...prev,
                ...fields
            };
            dataLogger.debug('Estado actual actualizado:', updatedData);
            return updatedData;
        });
    };

    const resetQuoteFormSummaryData = () => {
        setQuoteFormSummaryQuoteData({});
        dataLogger.info('Datos de Quote Form Summary resetados');
    };

    const saveQuoteFormSummaryQuote = async (id: string, data?: UpdateQuestionValuesQuoteFormSummaryRequest) => {
        try {
            apiLogger.info('Guardando datos Quote Form Summary para ID:', id, 'Datos:', data || quoteFormSummaryQuoteData);
            await upsertQuoteFormSummaryQuote(id, data || quoteFormSummaryQuoteData);
            apiLogger.info('Datos guardados exitosamente');
        } catch (error) {
            apiLogger.error('Error al guardar la cotización:', error);
            throw error;
        }
    };

    const value = {
        quoteFormSummaryQuoteData,
        updateQuoteFormSummaryField,
        updateMultipleQuoteFormSummaryFields,
        saveQuoteFormSummaryQuote,
        resetQuoteFormSummaryData,
        isCheckingQuoteFormSummaryStep,
        quoteFormSummaryStepChecked,
        quoteFormSummaryStep,
        isRedirectingToDigitalSignature,
        setIsRedirectingToDigitalSignature,
        isRedirectingToApplication
    };

    return (
        <QuoteFormSummaryContext.Provider value={value}>
            {children}
        </QuoteFormSummaryContext.Provider>
    );
}; 